import { Link } from 'react-router-dom';

/**
 * The mark: a split-flap tile holding an F, cut by one seam.
 *
 * The seam does two jobs — it is the letter's middle arm, and it is the fold
 * line of a departure-board cell, which is the same object CodeCells renders.
 * That is the whole idea: without the seam this is a fragment, not a letter.
 *
 * Butt caps and a miter corner, not the round ones the old flap A used. Chosen
 * by rasterizing both at 16px and inspecting the pixels magnified: above 32px
 * they are indistinguishable, but at 16px a round terminal bulbs the top arm
 * and the letter drifts toward a P. Flat strokes are also the truer split-flap
 * letterform — departure boards have no rounded ends.
 *
 * Drawn as geometry, never as text. The same paths ship as the favicon, and
 * SVG favicons render with external resource loading disabled, so a glyph set
 * in Bricolage would fall back to whatever the OS happens to have.
 *
 * Monochrome here on purpose. The favicon's seam is tungsten, because outside
 * the app the mark is identity; inside the app amber still means only one
 * thing, which is that something is live.
 */
export function Logo({ size = 21 }: { size?: number }) {
  return (
    <svg
      className="logo"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="64" height="64" rx="14" fill="var(--ink)" />
      <path
        d="M46 16 H22 V50"
        fill="none"
        stroke="var(--house)"
        strokeWidth="8.5"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />
      {/* y=32/h=4 is grid-fitted, not eyeballed: at 16px it lands on exactly
          one pixel row, so the seam stays a crisp line in a tab instead of a
          soft two-row band. Same reason it holds at 32, 64 and 128.

          Dead centre is slightly low for an F's middle arm, and y=28 would grid
          -fit too. It stays at 32 because the fold line is the load-bearing
          idea and split-flap cells fold at the middle — the letter bends to the
          fold, not the reverse. */}
      <rect y="32" width="64" height="4" fill="var(--house)" />
    </svg>
  );
}

/**
 * Mark plus wordmark. The wordmark stays live text, so the mark can be hidden
 * from assistive tech and the accessible name is just "FloorPoll".
 *
 * Both the console header and the auth card render this; pass `to` for the
 * linked variant.
 */
export function Brand({ to }: { to?: string }) {
  const content = (
    <>
      <Logo />
      FloorPoll
    </>
  );

  return to ? (
    <Link to={to} className="brand">
      {content}
    </Link>
  ) : (
    <div className="brand">{content}</div>
  );
}
